// Bloque «pdf»: descargar un documento (p. ej. el boletín) del registro
// elegido en el selector. Si el bloque lo pide, primero se elige un valor
// (p. ej. el parcial) o «todo». El servidor arma el PDF y devuelve los
// bytes; aquí se guardan como archivo. Los errores del servidor llegan en
// español y se muestran tal cual.

import { useState } from "react";
import { Download, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { siteErrorMessage, type SiteBlockOf } from "../values";
import { BlockTitle, siteFieldClass } from "./shared";

export interface SitePdfFile {
  fileName: string;
  bytes: ArrayBuffer;
}

/** Guarda los bytes como archivo (el navegador lo descarga). */
export function saveSiteFile({ fileName, bytes }: SitePdfFile) {
  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.rel = "noopener";
  document.body.appendChild(link);
  link.click();
  link.remove();
  // Safari necesita la URL un momento después del clic.
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

export function PdfBlock({
  block,
  onDownload,
  save = saveSiteFile,
}: {
  block: SiteBlockOf<"pdf">;
  /** `choice`: lo elegido (vacío = todo); sin `choose`, undefined. */
  onDownload: (choice: string | undefined) => Promise<SitePdfFile>;
  save?: (file: SitePdfFile) => void;
}) {
  const [choice, setChoice] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const selectId = `site-pdf-${block.key}-choice`;
  const ready = block.for !== null;

  const download = async () => {
    if (busy || !ready) return;
    setBusy(true);
    setError(null);
    try {
      save(await onDownload(block.choose ? choice : undefined));
    } catch (err) {
      setError(siteErrorMessage(err, "No pudimos descargar el PDF. Revisa tu conexión e intenta de nuevo."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="space-y-4 rounded-lg border bg-card p-4 @min-[40rem]:p-5" aria-label={block.title ?? block.label} data-testid={`site-pdf-${block.key}`}>
      <div className="flex items-start gap-3">
        <FileText className="mt-0.5 size-5 shrink-0 text-primary" aria-hidden />
        <div className="min-w-0 space-y-1">
          {block.title && <BlockTitle>{block.title}</BlockTitle>}
          <p className="text-sm text-muted-foreground">
            {ready ? (
              <>
                Para: <span className="font-medium text-foreground">{block.for}</span>
              </>
            ) : (
              "Elige primero de quién es el documento."
            )}
          </p>
        </div>
      </div>
      {block.choose && (
        <div className="space-y-1.5">
          <label htmlFor={selectId} className="block text-sm font-medium text-foreground">
            {block.choose.label}
          </label>
          <select
            id={selectId}
            className={siteFieldClass}
            value={choice}
            disabled={busy}
            onChange={(e) => setChoice(e.target.value)}
          >
            {block.choose.options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      )}
      {error && (
        <p className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive" role="alert">
          {error}
        </p>
      )}
      <Button type="button" className="h-11 w-full text-base @min-[40rem]:w-auto @min-[40rem]:px-6" disabled={busy || !ready} onClick={download}>
        {busy ? <Loader2 className="size-4 animate-spin" aria-hidden /> : <Download className="size-4" aria-hidden />}
        {busy ? "Preparando…" : block.label}
      </Button>
    </section>
  );
}
